import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/base/Seo";
import PageHeader from "@/components/feature/PageHeader";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const contactHeroImage =
  "https://readdy.ai/api/search-image?query=Warm%20editorial%20wide%20photograph%20of%20a%20volunteer%20in%20a%20red%20vest%20warmly%20shaking%20hands%20with%20a%20community%20member%20in%20a%20bright%20welcoming%20space%2C%20hopeful%20connection%20and%20solidarity%2C%20soft%20natural%20light%2C%20premium%20humanitarian%20photography%2C%20high%20detail&width=1600&height=500&seq=humanity59-contact-hero&orientation=landscape";

const FORM_ADDR = "https://readdy.ai/api/form/da2l69rd3r93mn1k3b80";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

export default function Contact() {
  const { status, message, handleSubmit } = useFormSubmit(FORM_ADDR);
  const { settings } = useSiteSettings();

  const contactInfos = [
    { label: "Email", value: settings?.email || "contact@humanity59.com", icon: "ri-mail-line" },
    { label: "Téléphone", value: settings?.phone || "06 18 23 21 63", icon: "ri-phone-line" },
    { label: "Adresse", value: settings?.address || "12 rue de la Solidarité, 59000 Lille", icon: "ri-map-pin-line" },
  ];

  const socials = [
    { label: "Instagram", icon: "ri-instagram-line", href: settings?.instagram || "https://www.instagram.com/humanity59_" },
    { label: "Snapchat", icon: "ri-snapchat-line", href: settings?.snapchat || "https://www.snapchat.com/@faz-humanity59" },
    { label: "WhatsApp", icon: "ri-whatsapp-line", href: settings?.whatsapp || "https://wa.me/33618232163" },
    { label: "YouTube", icon: "ri-youtube-line", href: settings?.youtube || "https://youtube.com/@humanity-59?si=-3aNepIejT2CzMCN" },
    { label: "Telegram", icon: "ri-telegram-line", href: settings?.telegram || "https://t.me/+3-x6F0OLfGg5MjM0" },
  ].filter((s) => s.href);

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title="Contact | Humanity59"
        description="Contactez Humanity59 à Lille par email, téléphone ou formulaire : questions sur les dons, le bénévolat ou les partenariats. Réponse sous 48h."
        keywords="contact, association humanitaire, Lille, bénévolat, partenariat"
        path="/contact"
      />
      <Header />
      <main className="flex-1">
        <PageHeader
          title="Contact"
          subtitle="Une question, une envie d'aider ou de devenir partenaire ? Nous serons ravis d'échanger avec vous."
          image={contactHeroImage}
        />

        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-12">
              <div className="lg:col-span-2">
                <span className="inline-block text-sm font-semibold text-primary-600">
                  Nos coordonnées
                </span>
                <h2 className="mt-3 font-heading text-3xl font-bold text-foreground-950">
                  Parlons ensemble
                </h2>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Que vous souhaitiez faire un don, rejoindre nos équipes ou
                  proposer un partenariat, n'hésitez pas à nous contacter. Nous
                  répondons généralement sous 48 heures.
                </p>

                <div className="mt-8 space-y-5">
                  {contactInfos.map((info) => (
                    <div key={info.label} className="flex items-center gap-4">
                      <span className="w-12 h-12 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className={`${info.icon} text-xl`}></i>
                      </span>
                      <div>
                        <p className="text-xs font-medium text-foreground-500">
                          {info.label}
                        </p>
                        <p className="text-sm font-semibold text-foreground-950">
                          {info.value}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8">
                  <p className="text-sm font-semibold text-foreground-950 mb-3">
                    Suivez-nous
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {socials.map((social) => (
                      <a
                        key={social.label}
                        href={social.href}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        aria-label={social.label}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
                      >
                        <i className={`${social.icon} text-lg`}></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <form
                  id="form-contact"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8"
                >
                  <h3 className="font-heading text-xl font-semibold text-foreground-950">
                    Envoyez-nous un message
                  </h3>

                  <input
                    type="text"
                    name="company_alt"
                    className="field-offscreen"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="nom"
                        className="block text-sm font-medium text-foreground-700 mb-1.5"
                      >
                        Nom complet
                      </label>
                      <input
                        id="nom"
                        name="nom"
                        type="text"
                        required
                        placeholder="Votre nom"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-foreground-700 mb-1.5"
                      >
                        Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="vous@exemple.fr"
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="mt-5">
                    <label
                      htmlFor="sujet"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Sujet
                    </label>
                    <input
                      id="sujet"
                      name="sujet"
                      type="text"
                      required
                      placeholder="L'objet de votre message"
                      className={inputClass}
                    />
                  </div>

                  <div className="mt-5">
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      required
                      placeholder="Écrivez votre message…"
                      className={inputClass}
                    />
                  </div>

                  {status === "success" && (
                    <div className="mt-5 rounded-md bg-primary-100 text-primary-700 px-4 py-3 text-sm font-medium">
                      <i className="ri-checkbox-circle-line mr-1.5"></i>
                      {message}
                    </div>
                  )}
                  {status === "error" && (
                    <div className="mt-5 rounded-md bg-accent-100 text-accent-700 px-4 py-3 text-sm font-medium">
                      <i className="ri-error-warning-line mr-1.5"></i>
                      {message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-8 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "loading" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Envoi en cours…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Envoyer le message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-16 md:pb-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="rounded-lg overflow-hidden border border-background-200">
              <iframe
                title="Localisation d'Humanity59 à Lille"
                src="https://maps.google.com/maps?q=Lille%2C%20France&z=13&output=embed"
                className="w-full h-[360px] md:h-[420px] border-0"
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}